<template>
  <div class="gallery-image-container">
    <v-img 
      :src="image.url" 
      aspect-ratio="1.5" 
      class="gallery-image" 
      @click="openImageModal" 
      v-tooltip="formatTimestamp(image.created_at)"
    />
    <!-- New cover icon -->
    <v-icon 
      class="make-cover-icon" 
      color="primary" 
      @click.stop="isSetCoverModalVisible = true"
    >
      mdi-circle-outline
    </v-icon>
    <v-icon 
      class="delete-icon" 
      color="red" 
      @click.stop="isDeleteImageModalVisible = true"
    >
      mdi-trash-can
    </v-icon>

    <v-dialog v-model="isImageModalVisible" max-width="80%" @click:outside="closeImageModal">
      <v-card class="modal-card">
        <v-img 
          :src="image.url" 
          max-height="80vh" 
          max-width="80vw" 
          contain
        />
      </v-card>
    </v-dialog>

    <v-dialog v-model="isDeleteImageModalVisible" max-width="500px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          <p>Are you sure you want to delete this image?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="isDeleteImageModalVisible = false">Cancel</v-btn>
          <v-btn color="red darken-1" text @click="deleteImage">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isSetCoverModalVisible" max-width="500px">
      <v-card>
        <v-card-title>Set as Cover Image</v-card-title>
        <v-card-text>
          <p>Do you want to set this image as the cover?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="green darken-1" text @click="isSetCoverModalVisible = false">Cancel</v-btn>
          <v-btn color="primary darken-1" text @click="setAsCover">Set as Cover</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { usePopupStore } from "@/store";

export default {
  name: 'GalleryImage',
  props: {
    image: {
      type: Object,
      required: true,
    },
    aquariumId: { // Pass aquariumId as a prop
      type: [Number, String],
      required: true,
    },
  },
  setup() {
    const popupStore = usePopupStore();

    return {
      popupStore,
    };
  },
  data() {
    return {
      isImageModalVisible: false,
      isDeleteImageModalVisible: false,
      isSetCoverModalVisible: false, // New modal state
    };
  },
  methods: {
    formatTimestamp(ts) {
      if (!ts) return '';
      const date = new Date(ts);
      if (isNaN(date)) return ts;
      const pad = n => n.toString().padStart(2, '0');
      let hours = date.getHours();
      const minutes = pad(date.getMinutes());
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12;
      const year = date.getFullYear();
      const month = pad(date.getMonth() + 1);
      const day = pad(date.getDate());
      return `Uploaded on: ${year}-${month}-${day} ${pad(hours)}:${minutes} ${ampm}`;
    },
    openImageModal() {
      this.isImageModalVisible = true;
    },
    closeImageModal() {
      this.isImageModalVisible = false;
    },
    async deleteImage() {
      try {
        const response = await fetch(`/api/images/${this.image.id}`, {
          method: 'DELETE',
        });
        if (!response.ok) {
          this.popupStore.setMessage('Failed to delete', 'Bad response from server', 'error');
          return;
        }

        this.popupStore.setMessage('Image deleted', 'Hope you saved a backup!', 'success');
        this.$eventBus.emit('image-deleted');
        this.isDeleteImageModalVisible = false;

      } catch (error) {
        this.popupStore.setMessage('Failed to delete', 'Failed to delete image', 'error');
      }
    },
    async setAsCover() {
      if (!this.aquariumId) {
        this.popupStore.setMessage('Error updating aquarium', 'aquariumId prop is missing in GalleryImage component', 'error');
        return;
      }
      try {
        const response = await axios.put(
          `/api/aquariums/${this.aquariumId}`,
          JSON.stringify({ image_url: this.image.url }), // stringify payload
          { headers: { 'Content-Type': 'application/json' } }
        );

        this.popupStore.setMessage('Cover image updated', 'This image is now the face of your aquarium!', 'success');
        this.$eventBus.emit('cover-image-set');
        this.isSetCoverModalVisible = false;

      } catch (error) {
        this.popupStore.setMessage('Error updating aquarium', error.response?.data?.message || error.message);
      }
    },
  },
};
</script>

<style scoped>
.gallery-image-container {
  position: relative;
  cursor: pointer;
}

.make-cover-icon {
  position: absolute;
  top: 0px;
  right: 0px;
  border-radius: 50%;
  padding: 5px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  z-index: 2;
}

.delete-icon {
  position: absolute;
  bottom: 0px;
  right: 0px;
  border-radius: 50%;
  padding: 5px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
  z-index: 2;
}

.gallery-image-container:hover .delete-icon,
.gallery-image-container:hover .make-cover-icon {
  opacity: 1;
}

.gallery-image {
  border: 1px solid rgb(var(--v-theme-border));
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
}

.gallery-image:hover {
  transform: scale(1.25);
  transition: transform 0.2s ease-in-out;
}
</style>
